import { useEffect, useState } from 'react';
import { ScrollHandler } from 'react-virtualized';

import { DefaultAutocompleteOption } from '../../../../api/autocomplete/types/DefaultAutocompleteOption';
import { BaseAutocompleteParams } from '../../../../api/autocomplete/types/BaseAutocompleteParams';
import { AutocompleteResponse } from '../../../../api/autocomplete/types/AutocompleteResponse';
import { AutocompleteApiCall } from '../../../../api/autocomplete/types/AutocompleteApiCall';
import { AutocompleteParams } from '../../../../api/autocomplete/types/AutocompleteParams';
import { useCancellableRequest } from '../../../../services/cancellable-request/hook';
import { AfterKey } from '../../../../api/autocomplete/types/AfterKey';

export const MIN_QUERY_LENGTH = 2;
const ITEM_SIZE = 46;
export const PER_PAGE = 25;

export const useOptionsHandler = <
  OptionT = DefaultAutocompleteOption,
  ParamsT extends BaseAutocompleteParams = BaseAutocompleteParams
>(
    apiCall: AutocompleteApiCall<ParamsT, OptionT>,
    apiCallParams?: Omit<AutocompleteParams<ParamsT>, 'bucket_size'>,
    optionsModifier?: (options: OptionT[]) => OptionT[],
  ) => {
  const [query, setQuery] = useState('');
  const [loading, setLoading] = useState(true);
  const [options, setOptions] = useState<OptionT[] | null>(null);
  const [cachedOptions, setCachedOptions] = useState<OptionT[] | null>(null);
  const [afterKey, setAfterKey] = useState<AfterKey>(undefined);
  const [cachedAfterKey, setCachedAfterKey] = useState<AfterKey>(undefined);

  const request = useCancellableRequest();

  useEffect(() => {
    if (query.length >= MIN_QUERY_LENGTH) {
      const timer = setTimeout(() => fetchOptions({ isInitLoad: false }), 1000);
      return () => clearTimeout(timer);
    }
  }, [query, apiCall]);

  useEffect(() => {
    fetchOptions({ isInitLoad: true });
  }, [apiCallParams, apiCall]);

  const fetchOptions = ({ isInitLoad, isLoadMore }: { isInitLoad?: boolean; isLoadMore?: boolean }) => {
    setLoading(true);
    if (isInitLoad) setAfterKey(undefined);

    const reqParams = {
      ...apiCallParams,
      query,
      after_key: isInitLoad ? undefined : afterKey,
      bucket_size: PER_PAGE
    };

    const handleResponse = (response: AutocompleteResponse<OptionT> | OptionT[]) => {
      if ('after_key' in response) {
        const optionsFromNewEndpoints = optionsModifier ? optionsModifier(response.data) : response.data;
        setOptions(prevOptions => (isLoadMore && prevOptions) ?
          [...prevOptions, ...optionsFromNewEndpoints]
          : optionsFromNewEndpoints
        );
        setAfterKey(response.after_key);
        if (isInitLoad) {
          setCachedOptions(optionsFromNewEndpoints);
          setCachedAfterKey(response.after_key);
        }
      } else if (isInitLoad) { // to support old endpoints
        setOptions(null);
      } else {
        const noAfterKeyResponse = response as OptionT[];
        const optionsFromOldEndpoints = optionsModifier ? optionsModifier(noAfterKeyResponse) : noAfterKeyResponse;
        setOptions(optionsFromOldEndpoints);
      }
  
      setLoading(false);
    };

    return request.send(apiCall, reqParams as ParamsT)
      .then(handleResponse)
      .catch((err) => {
        if (err.isAxiosError) setLoading(false);
      });
  };

  const handleQueryChange = (newQuery: string) => {
    if (newQuery.length < MIN_QUERY_LENGTH) {
      if (!newQuery) {
        setOptions(cachedOptions);
        setAfterKey(cachedAfterKey);
      } else {
        setOptions(null);
      }
    } else {
      setAfterKey(undefined);
    }
    setQuery(newQuery);
  };

  const handleScroll: ScrollHandler = ({ scrollTop, clientHeight, scrollHeight }) => {
    const scrollEnd = scrollTop > 0 && (scrollTop + clientHeight + (ITEM_SIZE * 4) >= scrollHeight);
    if (afterKey && scrollEnd && !loading && !query) fetchOptions({ isLoadMore: true });
  };

  return {
    options,
    query,
    loading,
    fetchOptionsByQuery: handleQueryChange,
    handleScroll
  };
};
